/**
 * Medical Conditions & Procedures Data
 * 
 * Pre-configured condition and procedure schemas for each dental service.
 * Use these with getMedicalConditionLD() and getMedicalProcedureLD().
 */

import { PUBLIC_SITE_URL } from './seo';

// ============================================================================
// INVISALIGN / ORTHODONTICS
// ============================================================================

export const invisalignCondition = {
  name: 'Malocclusion',
  treatmentName: 'Invisalign Clear Aligner Treatment',
  signOrSymptom: [
    'Crooked teeth',
    'Overcrowding',
    'Gaps between teeth',
    'Overbite or underbite',
    'Difficulty cleaning teeth',
  ],
  possibleTreatment: `${PUBLIC_SITE_URL}/invisalign-acton`,
  riskFactor: [
    'Genetics',
    'Thumb sucking in childhood',
    'Premature tooth loss',
    'Jaw injury',
  ],
  associatedAnatomy: 'Teeth and Jaw',
};

export const invisalignProcedure = {
  name: 'Invisalign Clear Aligner Treatment',
  alternateName: ['Clear Aligners', 'Invisible Braces', 'Invisalign'],
  procedureType: 'Orthodontic',
  url: `${PUBLIC_SITE_URL}/invisalign-acton`,
  howPerformed: 'Custom clear plastic aligners gradually shift teeth into desired position. Aligners are changed every 1-2 weeks, worn 20-22 hours per day. Digital treatment planning ensures precise tooth movement.',
  preparation: 'Initial consultation with 3D scan, X-rays, and digital treatment planning. Impressions taken for custom aligner fabrication.',
  followup: 'Check-up appointments every 6-8 weeks to monitor progress. Retainers worn after treatment to maintain results.',
  bodyLocation: 'Teeth and Jaw',
  expectedPrognosis: 'Straighter teeth, improved bite, enhanced smile aesthetics. Treatment typically takes 6-18 months with 95% success rate for suitable candidates.',
};

// ============================================================================
// DENTAL IMPLANTS
// ============================================================================

export const implantsCondition = {
  name: 'Missing Teeth',
  treatmentName: 'Dental Implant Placement',
  signOrSymptom: [
    'Gap in smile',
    'Difficulty chewing',
    'Bone loss in jaw',
    'Shifting of adjacent teeth',
    'Changes in facial structure',
  ],
  possibleTreatment: `${PUBLIC_SITE_URL}/implants-acton`,
  riskFactor: [
    'Tooth decay',
    'Gum disease',
    'Trauma or injury',
    'Failed root canal',
    'Congenital absence',
  ],
  associatedAnatomy: 'Tooth and Jawbone',
};

export const implantsProcedure = {
  name: 'Dental Implant Placement',
  alternateName: ['Tooth Implant', 'Titanium Implant', 'Dental Prosthesis'],
  procedureType: 'Surgical',
  url: `${PUBLIC_SITE_URL}/implants-acton`,
  howPerformed: 'Titanium post surgically placed into jawbone under local anesthetic. Post integrates with bone (osseointegration) over 3-6 months. Abutment and crown attached to complete restoration.',
  preparation: '3D CT scan, bone density assessment, medical history review. Bone grafting may be required if insufficient bone present.',
  followup: 'Regular check-ups to monitor healing and integration. Professional cleanings every 6 months. Good oral hygiene essential for longevity.',
  bodyLocation: 'Jawbone and Gum',
  expectedPrognosis: 'Permanent tooth replacement with 95%+ success rate. Implants can last 20-30 years or lifetime with proper care. Restores full chewing function.',
};

// ============================================================================
// COMPOSITE BONDING
// ============================================================================

export const compositeBondingCondition = {
  name: 'Tooth Discoloration and Damage',
  treatmentName: 'Composite Bonding',
  signOrSymptom: [
    'Chipped or cracked teeth',
    'Discolored enamel',
    'Gaps between teeth',
    'Uneven tooth length',
    'Minor misalignment',
  ],
  possibleTreatment: `${PUBLIC_SITE_URL}/composite-bonding-acton`,
  riskFactor: [
    'Tooth decay',
    'Trauma or injury',
    'Staining from food/drinks',
    'Aging',
    'Genetics',
  ],
  associatedAnatomy: 'Tooth Enamel',
};

export const compositeBondingProcedure = {
  name: 'Composite Bonding',
  alternateName: ['Dental Bonding', 'Tooth Bonding', 'Cosmetic Bonding'],
  procedureType: 'Cosmetic',
  url: `${PUBLIC_SITE_URL}/composite-bonding-acton`,
  howPerformed: 'Tooth-colored composite resin applied directly to tooth surface. Material sculpted, shaped, and hardened with UV light. Minimal tooth preparation required.',
  preparation: 'Color matching to existing teeth. Mild tooth etching for bonding surface. No anesthetic usually needed.',
  followup: 'Avoid hard foods and staining agents for 48 hours. Regular dental cleanings maintain appearance. Touch-ups may be needed after 5-10 years.',
  bodyLocation: 'Tooth Enamel',
  expectedPrognosis: 'Immediate aesthetic improvement. Bonding lasts 5-10 years with proper care. Completely reversible and non-invasive.',
};

// ============================================================================
// COSMETIC DENTISTRY / VENEERS
// ============================================================================

export const veneersCondition = {
  name: 'Aesthetic Dental Concerns',
  treatmentName: 'Porcelain Veneers',
  signOrSymptom: [
    'Severely discolored teeth',
    'Misshapen teeth',
    'Worn enamel',
    'Uneven teeth',
    'Large gaps',
  ],
  possibleTreatment: `${PUBLIC_SITE_URL}/cosmetic-dentist-acton`,
  riskFactor: [
    'Genetics',
    'Aging',
    'Teeth grinding',
    'Staining substances',
    'Enamel erosion',
  ],
  associatedAnatomy: 'Tooth Enamel',
};

export const veneersProcedure = {
  name: 'Porcelain Veneer Placement',
  alternateName: ['Dental Veneers', 'Ceramic Veneers', 'Smile Makeover'],
  procedureType: 'Cosmetic',
  url: `${PUBLIC_SITE_URL}/cosmetic-dentist-acton`,
  howPerformed: 'Thin porcelain shells custom-crafted to cover front of teeth. Small amount of enamel removed. Veneers bonded permanently to tooth surface.',
  preparation: 'Digital smile design, tooth preparation (0.5mm enamel removal), impressions for lab fabrication. Temporary veneers while permanent ones are made.',
  followup: 'Regular dental check-ups. Good oral hygiene. Avoid biting hard objects. Nightguard if teeth grinding present.',
  bodyLocation: 'Tooth Enamel',
  expectedPrognosis: 'Dramatic smile transformation. Veneers last 10-20 years. Natural appearance, stain-resistant, and durable.',
};

// ============================================================================
// EMERGENCY DENTISTRY / TOOTH EXTRACTION
// ============================================================================

export const emergencyCondition = {
  name: 'Acute Dental Pain',
  treatmentName: 'Emergency Dental Treatment',
  signOrSymptom: [
    'Severe toothache',
    'Swelling or abscess',
    'Broken tooth',
    'Knocked-out tooth',
    'Uncontrolled bleeding',
  ],
  possibleTreatment: `${PUBLIC_SITE_URL}/emergency-dentist-acton`,
  riskFactor: [
    'Untreated tooth decay',
    'Advanced gum disease',
    'Trauma or injury',
    'Failed dental work',
    'Infection',
  ],
  associatedAnatomy: 'Tooth and Surrounding Tissue',
};

export const emergencyProcedure = {
  name: 'Emergency Dental Treatment',
  alternateName: ['Urgent Dental Care', 'Same-Day Dentistry', 'Acute Care'],
  procedureType: 'Emergency',
  url: `${PUBLIC_SITE_URL}/emergency-dentist-acton`,
  howPerformed: 'Immediate pain relief, infection control, and stabilization. Treatment may include root canal, extraction, repair of broken tooth, or abscess drainage.',
  preparation: 'Minimal preparation required. Call ahead for same-day appointment. Bring list of medications and medical history.',
  followup: 'Follow-up appointments for definitive treatment. Pain management and antibiotics if needed. Prevention counseling.',
  bodyLocation: 'Mouth',
  expectedPrognosis: 'Immediate pain relief and infection control. Long-term prognosis depends on specific condition and treatment provided.',
};

// ============================================================================
// GENERAL DENTISTRY / PREVENTIVE CARE
// ============================================================================

export const preventiveCareCondition = {
  name: 'Dental Plaque and Calculus',
  treatmentName: 'Professional Dental Cleaning',
  signOrSymptom: [
    'Plaque buildup',
    'Tartar deposits',
    'Gum inflammation',
    'Bad breath',
    'Bleeding gums',
  ],
  possibleTreatment: `${PUBLIC_SITE_URL}/dentist-acton`,
  riskFactor: [
    'Poor oral hygiene',
    'Irregular dental visits',
    'Diet high in sugar',
    'Smoking',
    'Dry mouth',
  ],
  associatedAnatomy: 'Teeth and Gums',
};

export const preventiveCareProcedure = {
  name: 'Professional Dental Cleaning and Examination',
  alternateName: ['Dental Checkup', 'Hygiene Appointment', 'Scale and Polish'],
  procedureType: 'Preventive',
  url: `${PUBLIC_SITE_URL}/dentist-acton`,
  howPerformed: 'Thorough examination of teeth, gums, and oral tissues. Removal of plaque and tartar. Polishing to remove surface stains. Fluoride treatment if needed.',
  preparation: 'No special preparation needed. Brush and floss before appointment. Bring insurance information and medical history.',
  followup: 'Return every 6 months for routine cleaning. Daily brushing twice and flossing once. Follow any specific recommendations from dentist.',
  bodyLocation: 'Mouth',
  expectedPrognosis: 'Maintains oral health, prevents cavities and gum disease. Early detection of potential problems. Fresh breath and cleaner teeth.',
};

// ============================================================================
// TEETH WHITENING
// ============================================================================

export const whiteningCondition = {
  name: 'Tooth Discoloration',
  treatmentName: 'Professional Teeth Whitening',
  signOrSymptom: [
    'Yellow or stained teeth',
    'Brown discoloration',
    'Age-related yellowing',
    'Uneven tooth color',
  ],
  possibleTreatment: `${PUBLIC_SITE_URL}/cosmetic-dentist-acton`,
  riskFactor: [
    'Coffee and tea consumption',
    'Red wine',
    'Smoking',
    'Aging',
    'Certain medications',
  ],
  associatedAnatomy: 'Tooth Enamel',
};

export const whiteningProcedure = {
  name: 'Professional Teeth Whitening',
  alternateName: ['Tooth Bleaching', 'Smile Brightening', 'Dental Whitening'],
  procedureType: 'Cosmetic',
  url: `${PUBLIC_SITE_URL}/cosmetic-dentist-acton`,
  howPerformed: 'Professional-strength whitening gel applied to teeth. Light activation may be used. Treatment takes 60-90 minutes in-office or 2-4 weeks with custom trays at home.',
  preparation: 'Professional cleaning recommended first. Check for cavities or gum disease. Avoid staining foods/drinks 24 hours before.',
  followup: 'Avoid staining substances for 48 hours. Use whitening toothpaste to maintain results. Touch-up treatments every 6-12 months if needed.',
  bodyLocation: 'Tooth Enamel',
  expectedPrognosis: 'Teeth 2-8 shades whiter. Results last 1-3 years with good care. Safe and effective when done professionally.',
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export default {
  // Invisalign
  invisalignCondition,
  invisalignProcedure,
  
  // Dental Implants
  implantsCondition,
  implantsProcedure,
  
  // Composite Bonding
  compositeBondingCondition,
  compositeBondingProcedure,
  
  // Veneers / Cosmetic
  veneersCondition,
  veneersProcedure,
  
  // Emergency
  emergencyCondition,
  emergencyProcedure,
  
  // Preventive / General
  preventiveCareCondition,
  preventiveCareProcedure,
  
  // Whitening
  whiteningCondition,
  whiteningProcedure,
};
